import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Camera, Upload, Trash2, X, Images } from "lucide-react";
import { toast } from "sonner";

const KIND_STYLES = {
  before: "bg-[#3a2420] text-[#e56b42]",
  after: "bg-[#1e3b2d] text-[#b0c0b6]",
  general: "bg-[#233129] text-[#8ba394]",
};

function AuthedImage({ photoId, alt, className, onClick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/photos/${photoId}/file`, { responseType: "blob" });
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photoId]);

  if (!src) {
    return <div className={`${className} bg-[#0a0f0d] animate-pulse`} />;
  }
  return <img src={src} alt={alt} className={className} onClick={onClick} />;
}

export default function PhotoGallery({ open, onOpenChange, jobId, target, customerName, onChanged }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [pendingKind, setPendingKind] = useState("before");

  // Resolve target: explicit `target` prop wins, otherwise fall back to legacy `jobId`
  const t = target || (jobId ? { type: "job", id: jobId } : null);
  const isInvoice = t?.type === "invoice";
  const listUrl = t ? (isInvoice ? `/invoices/${t.id}/photos` : `/jobs/${t.id}/photos`) : null;
  const uploadUrl = t ? (isInvoice ? `/invoices/${t.id}/photos` : `/jobs/${t.id}/photos`) : null;

  const load = async () => {
    if (!listUrl) return;
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      setPhotos(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line
  }, [open, t?.id, t?.type]);

  const upload = async (files) => {
    if (!files || files.length === 0 || !uploadUrl) return;
    setUploading(true);
    let okCount = 0;
    const failures = [];
    try {
      for (const file of files) {
        if (!file.type.startsWith("image/") && !/\.(jpe?g|png|webp|gif|heic|heif)$/i.test(file.name)) {
          failures.push(`${file.name} is not an image`);
          continue;
        }
        try {
          const fd = new FormData();
          fd.append("file", file);
          fd.append("kind", pendingKind);
          fd.append("caption", "");
          await api.post(uploadUrl, fd);
          okCount += 1;
        } catch (err) {
          const detail = err.response?.data?.detail;
          const msg = typeof detail === "string" ? detail : err.message || "Upload failed";
          failures.push(`${file.name}: ${msg}`);
        }
      }
      if (okCount > 0) toast.success(`${okCount} photo${okCount === 1 ? "" : "s"} uploaded`);
      for (const f of failures) toast.error(f);
      await load();
      onChanged?.();
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const onDelete = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/photos/${photoId}`);
      toast.success("Photo deleted");
      await load();
      onChanged?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const byKind = {
    before: photos.filter((p) => p.kind === "before"),
    after: photos.filter((p) => p.kind === "after"),
    general: photos.filter((p) => p.kind === "general"),
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#141c18] border border-[#233129] text-[#eae6db] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black flex items-center gap-2">
              <Images className="w-5 h-5 text-[#e56b42]" />
              Job Photos — {customerName || "Job"}
            </DialogTitle>
            <DialogDescription className="text-[#8ba394] text-sm">
              Attach before / after photos as proof of service.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 flex-wrap border border-[#233129] p-3 rounded-sm">
            <label className="overline text-[10px]">Upload as:</label>
            {["before", "after", "general"].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setPendingKind(k)}
                className={`bm-badge ${pendingKind === k ? "bg-[#c83e2d] text-white" : "bg-[#233129] text-[#b0c0b6]"} cursor-pointer`}
                data-testid={`kind-${k}`}
              >
                {k}
              </button>
            ))}
            <div className="flex-1" />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => upload(Array.from(e.target.files || []))}
              data-testid="photo-file-input"
            />
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => upload(Array.from(e.target.files || []))}
              data-testid="photo-camera-input"
            />
            <button
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
              className="bm-btn bm-btn-secondary"
              data-testid="photo-take-button"
              title="Use your device camera (rear-facing on mobile)"
            >
              <Camera className="w-4 h-4" /> Take Photo
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="bm-btn bm-btn-primary"
              data-testid="photo-upload-button"
            >
              {uploading ? (
                <>Uploading…</>
              ) : (
                <>
                  <Upload className="w-4 h-4" /> Add Photos
                </>
              )}
            </button>
          </div>

          {loading ? (
            <div className="overline text-center py-8">Loading…</div>
          ) : photos.length === 0 ? (
            <div className="text-center py-10 text-[#8ba394]" data-testid="photo-gallery-empty">
              <Camera className="w-10 h-10 mx-auto mb-3 text-[#233129]" />
              <div className="text-sm">
                {isInvoice
                  ? "No photos yet. Add photos to support this invoice."
                  : "No photos yet. Snap a before/after to document the job."}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {["before", "after", "general"].map(
                (k) =>
                  byKind[k].length > 0 && (
                    <div key={k}>
                      <div className={`bm-badge ${KIND_STYLES[k]} mb-2`}>{k} · {byKind[k].length}</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {byKind[k].map((p) => (
                          <div
                            key={p.id}
                            className="relative group aspect-square bg-[#0a0f0d] border border-[#233129] overflow-hidden"
                            data-testid={`photo-${p.id}`}
                          >
                            <AuthedImage
                              photoId={p.id}
                              alt={p.caption || "Job photo"}
                              className="w-full h-full object-cover cursor-zoom-in"
                              onClick={() => setLightbox(p)}
                            />
                            <button
                              onClick={() => onDelete(p.id)}
                              className="absolute top-1 right-1 p-1 bg-black/70 rounded-sm text-[#e56b42] hover:bg-black opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`delete-photo-${p.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {lightbox && (
        <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
          <DialogContent className="bg-[#0a0f0d] border border-[#233129] max-w-5xl p-0 overflow-hidden">
            <DialogHeader className="sr-only">
              <DialogTitle>Photo preview</DialogTitle>
              <DialogDescription>Larger view of the selected job photo.</DialogDescription>
            </DialogHeader>
            <button
              onClick={() => setLightbox(null)}
              className="absolute top-2 right-2 z-10 p-2 bg-black/60 rounded-sm text-[#eae6db] hover:bg-black"
            >
              <X className="w-5 h-5" />
            </button>
            <AuthedImage photoId={lightbox.id} alt={lightbox.caption || "Job photo"} className="w-full max-h-[85vh] object-contain" />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
